"use client";

import * as React from "react";

type BodyScrollLockSnapshot = {
  scrollY: number;
  htmlOverflow: string;
  htmlOverscrollBehavior: string;
  bodyPosition: string;
  bodyTop: string;
  bodyLeft: string;
  bodyRight: string;
  bodyWidth: string;
  bodyOverflow: string;
  bodyOverscrollBehavior: string;
};

type BodyScrollLockReleaseOptions = {
  shouldRestoreScroll?: () => boolean;
};

let activeLockCount = 0;
let snapshot: BodyScrollLockSnapshot | null = null;

function setScrollLockedAttribute(locked: boolean) {
  document.documentElement.toggleAttribute("data-scroll-locked", locked);
  document.body.toggleAttribute("data-scroll-locked", locked);
}

function acquireBodyScrollLock() {
  if (activeLockCount === 0) {
    const html = document.documentElement;
    const { body } = document;
    const scrollY = window.scrollY;

    snapshot = {
      scrollY,
      htmlOverflow: html.style.overflow,
      htmlOverscrollBehavior: html.style.overscrollBehavior,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyLeft: body.style.left,
      bodyRight: body.style.right,
      bodyWidth: body.style.width,
      bodyOverflow: body.style.overflow,
      bodyOverscrollBehavior: body.style.overscrollBehavior,
    };

    html.style.overflow = "hidden";
    html.style.overscrollBehavior = "none";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";
    body.style.overflow = "hidden";
    body.style.overscrollBehavior = "none";
    setScrollLockedAttribute(true);
  }

  activeLockCount += 1;
  let released = false;

  return ({ shouldRestoreScroll }: BodyScrollLockReleaseOptions = {}) => {
    if (released) return;
    released = true;
    activeLockCount = Math.max(0, activeLockCount - 1);

    if (activeLockCount > 0 || !snapshot) return;

    const html = document.documentElement;
    const { body } = document;
    const currentSnapshot = snapshot;
    snapshot = null;

    html.style.overflow = currentSnapshot.htmlOverflow;
    html.style.overscrollBehavior = currentSnapshot.htmlOverscrollBehavior;
    body.style.position = currentSnapshot.bodyPosition;
    body.style.top = currentSnapshot.bodyTop;
    body.style.left = currentSnapshot.bodyLeft;
    body.style.right = currentSnapshot.bodyRight;
    body.style.width = currentSnapshot.bodyWidth;
    body.style.overflow = currentSnapshot.bodyOverflow;
    body.style.overscrollBehavior = currentSnapshot.bodyOverscrollBehavior;
    setScrollLockedAttribute(false);

    if (shouldRestoreScroll?.() ?? true) {
      window.scrollTo(0, currentSnapshot.scrollY);
    }
  };
}

export function useBodyScrollLock(
  locked: boolean,
  options: BodyScrollLockReleaseOptions = {},
) {
  const optionsRef = React.useRef(options);

  React.useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  React.useEffect(() => {
    if (!locked || typeof window === "undefined") return;

    const release = acquireBodyScrollLock();
    return () => release(optionsRef.current);
  }, [locked]);
}
